import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format } from 'date-fns';
import { fetchStats } from '../api';
import { scaleAggregateCost, usePricingVersion } from '../pricingOverrides';
import {
  CallSliceTable,
  ExecutionDurationPanel,
  StartupBreakdownPanel,
} from './PerformancePanels';
import { RetriesPanel } from './RetriesPanel';
import { buildLatencySeries, formatSeconds } from './latency';
import type { ApiEndpointModel, ApiStatsResponse } from '../types';

type RangeKey = '24h' | '7d' | '30d';
type IntervalKey = '5m' | '15m' | '1h' | '6h' | '1d';

const RANGE_LABELS: Record<RangeKey, string> = {
  '24h': 'Last 24h',
  '7d': '7 days',
  '30d': '30 days',
};

const RANGE_MS: Record<RangeKey, number> = {
  '24h': 24 * 60 * 60_000,
  '7d': 7 * 24 * 60 * 60_000,
  '30d': 30 * 24 * 60 * 60_000,
};

const INTERVAL_LABELS: Record<IntervalKey, string> = {
  '5m': '5 min',
  '15m': '15 min',
  '1h': '1 hour',
  '6h': '6 hours',
  '1d': '1 day',
};

const INTERVAL_MS: Record<IntervalKey, number> = {
  '5m': 5 * 60_000,
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '6h': 6 * 60 * 60_000,
  '1d': 24 * 60 * 60_000,
};

const DEFAULT_INTERVAL: Record<RangeKey, IntervalKey> = {
  '24h': '5m',
  '7d': '6h',
  '30d': '1d',
};

const COLOR_SUCCESS = '#70B77E';
const COLOR_ERROR = '#F0544F';

interface AnalyticsTabProps {
  model: ApiEndpointModel;
}

export function AnalyticsTab({ model }: AnalyticsTabProps) {
  const [rangeKey, setRangeKey] = useState<RangeKey>('24h');
  const [intervalKey, setIntervalKey] = useState<IntervalKey>('5m');
  const [data, setData] = useState<ApiStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const pricingVersion = usePricingVersion();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const to = Date.now();
    const from = to - RANGE_MS[rangeKey];
    fetchStats({ from, to, bucketSeconds: INTERVAL_MS[intervalKey] / 1000, modelType: model.key })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [model.key, rangeKey, intervalKey, reloadKey]);

  function selectRange(key: RangeKey) {
    setRangeKey(key);
    setIntervalKey(DEFAULT_INTERVAL[key]);
  }

  const totals = data?.totals;
  const total = totals?.total ?? 0;
  const successRate = totals && totals.total ? totals.success / totals.total : 0;

  // Merge the sparse server series into the full, evenly-spaced bucket range.
  const buckets = useMemo(() => {
    const step = INTERVAL_MS[intervalKey];
    const end = Date.now();
    const start = Math.floor((end - RANGE_MS[rangeKey]) / step) * step;
    const labelFmt =
      step >= INTERVAL_MS['1d'] ? 'MMM d' : rangeKey === '24h' ? 'HH:mm' : 'M/d HH:mm';

    const map = new Map<number, { success: number; error: number }>();
    for (let t = start; t <= end; t += step) map.set(t, { success: 0, error: 0 });

    for (const p of data?.series || []) {
      if (!p.ts) continue;
      const bucket = Math.floor(new Date(p.ts).getTime() / step) * step;
      const entry = map.get(bucket);
      if (!entry) continue;
      entry.success += p.success;
      entry.error += p.error;
    }

    return Array.from(map.entries()).map(([t, v]) => ({
      label: format(new Date(t), labelFmt),
      success: v.success,
      error: v.error,
    }));
  }, [data, rangeKey, intervalKey]);

  const recentErrors = data?.recent_errors || [];
  const slowest = data?.slowest || [];

  // Latency series share the bucket grid with the counts chart above.
  const bucketGrid = useMemo(() => {
    const step = INTERVAL_MS[intervalKey];
    const labelFmt =
      step >= INTERVAL_MS['1d'] ? 'MMM d' : rangeKey === '24h' ? 'HH:mm' : 'M/d HH:mm';
    return { step, rangeMs: RANGE_MS[rangeKey], labelFmt };
  }, [rangeKey, intervalKey]);

  const startupPoints = useMemo(
    () =>
      buildLatencySeries(data?.series, 'startup', bucketGrid.rangeMs, bucketGrid.step, bucketGrid.labelFmt),
    [data, bucketGrid],
  );
  const execPoints = useMemo(
    () =>
      buildLatencySeries(data?.series, 'exec', bucketGrid.rangeMs, bucketGrid.step, bucketGrid.labelFmt),
    [data, bucketGrid],
  );

  const effectiveCost = useMemo(() => {
    void pricingVersion; // recompute when the pricing override changes
    return scaleAggregateCost(model, totals?.cost_usd ?? null);
  }, [model, totals, pricingVersion]);

  return (
    <div className="space-y-4">
      {/* Header + controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">Analytics</h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            {(Object.keys(RANGE_LABELS) as RangeKey[]).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => selectRange(key)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  rangeKey === key
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {RANGE_LABELS[key]}
              </button>
            ))}
          </div>
          <select
            value={intervalKey}
            onChange={(e) => setIntervalKey(e.target.value as IntervalKey)}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            title="Bucket interval"
          >
            {(Object.keys(INTERVAL_LABELS) as IntervalKey[]).map((key) => (
              <option key={key} value={key}>
                {INTERVAL_LABELS[key]}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            title="Refresh"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 4v5h5M20 20v-5h-5M5 19a9 9 0 0014-6M19 5a9 9 0 00-14 6" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm">
          Failed to load analytics: {error}
        </div>
      )}

      {/* Summary stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard
          label="Total requests"
          value={String(total)}
          sub={`${Math.round(successRate * 100)}% success rate`}
        />
        <StatCard label="Execution p99" value={formatSeconds(totals?.p99 ?? null)} sub="99% are faster" />
        <StatCard label="Execution p95" value={formatSeconds(totals?.p95 ?? null)} sub="95% are faster" />
        <StatCard label="Execution p90" value={formatSeconds(totals?.p90 ?? null)} sub="90% are faster" />
        <StatCard label="Execution p75" value={formatSeconds(totals?.p75 ?? null)} sub="75% are faster" />
        <StatCard
          label="Total cost"
          value={
            effectiveCost.cost && effectiveCost.cost > 0
              ? `$${effectiveCost.cost.toFixed(4)}${effectiveCost.estimated ? ' est.' : ''}`
              : '$0'
          }
          sub={effectiveCost.estimated ? 'estimated (tuned pricing)' : 'in this period'}
        />
      </div>

      {/* Request counts chart */}
      <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Request counts</h3>
          <div className="text-right">
            <span className="text-lg font-bold text-slate-700">{total}</span>
            <span className="ml-1 text-[10px] text-slate-500 uppercase">total</span>
          </div>
        </div>
        <div className="h-64">
          {loading && !data ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">
              Loading analytics…
            </div>
          ) : total > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={buckets} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#64748b' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    fontSize: '12px',
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  height={24}
                  formatter={(value: string) => <span className="text-slate-600 text-xs">{value}</span>}
                />
                <Bar dataKey="success" name="Success" stackId="a" fill={COLOR_SUCCESS} />
                <Bar dataKey="error" name="Error" stackId="a" fill={COLOR_ERROR} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-slate-400">
              No requests in this period
            </div>
          )}
        </div>
      </div>

      {/* Latency: the wait before a request runs, then the run itself */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StartupBreakdownPanel data={data} points={startupPoints} loading={loading} />
        <ExecutionDurationPanel data={data} points={execPoints} loading={loading} />
      </div>

      {/* Row-level outliers, with the retry split under the errors it explains */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <CallSliceTable
          title="Slower requests"
          rows={slowest}
          loading={loading}
          emptyLabel="No completed requests in this period"
          showModel={false}
          right={
            <div className="text-right shrink-0">
              <span className="text-lg font-bold text-slate-700 tabular-nums">
                {formatSeconds(totals?.p90 ?? null)}
              </span>
              <span className="ml-1 text-[10px] text-slate-500 uppercase">p90</span>
            </div>
          }
        />
        <div className="space-y-4">
          <CallSliceTable
            title="Recent errors"
            rows={recentErrors}
            loading={loading}
            emptyLabel="No errors in this period"
            emptyTone="success"
            showModel={false}
            right={
              <div className="text-right shrink-0">
                <span className="text-lg font-bold text-danger-700 tabular-nums">
                  {totals?.errors ?? 0}
                </span>
                <span className="ml-1 text-[10px] text-slate-500 uppercase">total</span>
              </div>
            }
          />
          <RetriesPanel groups={data?.retry_groups} loading={loading} />
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}
